/*
 * Governance Tile · Phase 7 · Severity counters + task-card grid.
 *
 * Ports mockup v8.8.12 lines 1424-1600 into React.  Three sub-blocks:
 *   1. Section-title strip · turn count binds live to tasks_total.
 *   2. Severity counter row · four boxes (Sev 0 · Sev 1 · Sev 2 ·
 *      Sev 3).  Each count binds via SEV_MAP; every counter is
 *      currently pending IJ-034 / IJ-045 so renders em dash with a
 *      `-pending` testid marker (attestation-honesty rule · anchor
 *      GB-013 · § V.6).
 *   3. Task-card grid · empty-state overlay when the endpoint does
 *      not yet emit `task_cards[]` (current state).  When cards
 *      arrive the block will render them via the same live-wire
 *      pattern as the direction chart's per-turn events.
 *
 * IJ-087 · 2026-07-24 · Phase 7 of the v4-to-v8.8.12 re-cut.
 */
import {
  TASKS_SECTION_LABEL_PREFIX,
  TASKS_SECTION_LABEL_SUFFIX,
  TASKS_SECTION_LABEL_ZERO,
  SEVERITY_COUNTERS,
  TASKS_FILTER_HINT,
  TASKS_EMPTY_TITLE,
  TASKS_EMPTY_BODY,
  ACTOR_COUNTERS,
  ACTOR_STRIP_HEADING,
} from './copy/tasksBlock';

const PENDING = '\u2014';
const PENDING_TOOLTIP =
  'Not measured yet. Live counter pending IJ-034 / IJ-045 (severity aggregation service).';

// key -> extractor(summary) returning {value, tone} or null (pending).
// IJ-430 (2026-08-29) · GB-127 fix · the severity row now reads the
// Atlas-sourced summary.sev_atlas (lifetime totals) so it matches the
// EBTA Errors page Sev0/Sev1/Sev2 chips exactly. Three tiers, Atlas scale
// (Sev2 = most severe / red). Replaces the old .today four-tier binding
// that rendered 0/0/0/0 for historical breaches.
const SEV_MAP = {
  'sev-0': (s) => {
    const v = s.sev_atlas?.sev0;
    if (v === undefined || v === null) return null;
    return { value: String(v), tone: 'slate' };
  },
  'sev-1': (s) => {
    const v = s.sev_atlas?.sev1;
    if (v === undefined || v === null) return null;
    return { value: String(v), tone: 'amber' };
  },
  'sev-2': (s) => {
    const v = s.sev_atlas?.sev2;
    if (v === undefined || v === null) return null;
    return { value: String(v), tone: v > 0 ? 'red' : 'slate' };
  },
};

function toneToStyle(tone) {
  switch (tone) {
    case 'red':
      return 'border-red-500/30 bg-red-500/10 text-red-300';
    case 'rose':
      return 'border-rose-500/30 bg-rose-500/10 text-rose-300';
    case 'amber':
      return 'border-amber-500/40 bg-amber-500/15 text-amber-300';
    default:
      return 'border-slate-500/30 bg-slate-500/10 text-slate-300';
  }
}

function BadgeChip({ badge }) {
  const tone = badge.tone || 'slate';
  const cls =
    tone === 'green'
      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
      : tone === 'red'
        ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
        : tone === 'amber'
          ? 'border-amber-500/40 bg-amber-500/15 text-amber-300'
          : 'border-slate-500/30 bg-slate-500/10 text-slate-300';
  // IJ-122 · LESSON-017 · allow badge text to wrap so long labels do
  // not push out of the card container (GB-051 · card badges floated
  // outside boundaries).  Whitespace-normal keeps multi-word badges
  // inside their own container.
  return (
    <span
      className={
        'text-[10px] font-medium px-2 py-0.5 rounded border whitespace-normal break-words ' + cls
      }
    >
      {badge.label}
    </span>
  );
}

function TaskCard({ card }) {
  const borderTone = card.tone || 'slate';
  const borderCls =
    borderTone === 'green'
      ? 'border-l-emerald-500/60'
      : borderTone === 'red'
        ? 'border-l-rose-500/60'
        : borderTone === 'amber'
          ? 'border-l-amber-500/60'
          : 'border-l-slate-600/60';
  // IJ-122 · LESSON-017 · min-w-0 on the card and its header prevents
  // long slug or badge content from forcing the card to overflow its
  // grid column (GB-050 · cards overlap horizontally · GB-052 · text
  // truncated on right edge).  break-words on text-bearing elements
  // ensures no long token forces a horizontal scroll.
  return (
    <article
      data-testid={`governance-tile-task-card-${card.id}`}
      className={
        'min-w-0 rounded-lg border border-l-4 border-slate-700 bg-slate-900/40 p-3 text-[12px] text-slate-200 ' +
        borderCls
      }
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2 mb-2 min-w-0">
        <div className="flex items-baseline gap-2 min-w-0 flex-shrink">
          <span className="font-mono text-[11px] text-cyan-300">{card.id}</span>
          <span className="text-slate-300 break-words">{card.slug}</span>
        </div>
        <div className="flex flex-wrap justify-end gap-1 max-w-full">
          {(card.badges || []).map((b, i) => (
            <BadgeChip key={i} badge={b} />
          ))}
        </div>
      </header>
      {card.operator_said ? (
        <p className="mb-1 break-words">
          <span className="text-slate-400 mr-1">You said:</span>
          {card.operator_said}
        </p>
      ) : null}
      {card.e1_did ? (
        <p className="mb-1 break-words">
          <span className="text-slate-400 mr-1">I did:</span>
          {card.e1_did}
        </p>
      ) : null}
      {card.judge ? (
        <p className="mb-1 break-words">
          <span className="text-slate-400 mr-1">Judge:</span>
          {card.judge}
        </p>
      ) : null}
    </article>
  );
}

function SeverityCounter({ counter, summary }) {
  const extractor = SEV_MAP[counter.key];
  let value = PENDING;
  let tone = counter.tone;
  let tooltip = counter.tooltip;
  let pending = true;

  if (extractor === null || extractor === undefined) {
    tooltip = `${counter.tooltip} \u00B7 ${PENDING_TOOLTIP}`;
  } else if (!summary) {
    // Summary still loading — keep pending render.
    pending = true;
  } else {
    const r = extractor(summary);
    if (r) {
      value = r.value;
      tone = r.tone;
      pending = false;
    } else {
      // Endpoint did not emit sev_counters (backward-compat).
      tooltip = `${counter.tooltip} \u00B7 ${PENDING_TOOLTIP}`;
    }
  }

  const testId = pending
    ? `governance-tile-severity-${counter.key}-pending`
    : `governance-tile-severity-${counter.key}`;
  return (
    <div
      data-testid={testId}
      title={tooltip}
      className={
        'text-center p-2 rounded border cursor-help ' + toneToStyle(tone)
      }
    >
      <div className="text-[22px] font-bold tabular-nums leading-none">{value}</div>
      <div className="mt-1 text-[10px] tracking-wide font-semibold">
        {counter.label}
      </div>
    </div>
  );
}

// IJ-431 · per-actor error strip · reads summary.actor_atlas (Atlas-sourced).
const ACTOR_MAP = {
  e1: (s) => s.actor_atlas?.e1,
  judge: (s) => s.actor_atlas?.judge,
  reply_guard: (s) => s.actor_atlas?.reply_guard,
  operator_caught: (s) => s.actor_atlas?.operator_caught,
};

function ActorCounter({ counter, summary }) {
  const raw = summary ? ACTOR_MAP[counter.key]?.(summary) : undefined;
  const pending = raw === undefined || raw === null;
  const value = pending ? PENDING : String(raw);
  const tone = !pending && raw > 0 ? counter.tone : 'slate';
  const testId = pending
    ? `governance-tile-actor-${counter.key}-pending`
    : `governance-tile-actor-${counter.key}`;
  return (
    <div
      data-testid={testId}
      className={'text-center p-2 rounded border ' + toneToStyle(tone)}
    >
      <div className="text-[22px] font-bold tabular-nums leading-none">{value}</div>
      <div className="mt-1 text-[10px] tracking-wide font-semibold">
        {counter.label}
      </div>
    </div>
  );
}

export default function TasksBlock({ summary, loading, error }) {
  const workingSummary = error ? null : summary;
  const tasksTotal = workingSummary?.tasks_total ?? null;
  const cards = Array.isArray(workingSummary?.task_cards)
    ? workingSummary.task_cards
    : [];

  let sectionTitle;
  if (tasksTotal === null) {
    sectionTitle = `${TASKS_SECTION_LABEL_PREFIX}${PENDING}${TASKS_SECTION_LABEL_SUFFIX}`;
  } else if (tasksTotal === 0) {
    sectionTitle = TASKS_SECTION_LABEL_ZERO;
  } else {
    sectionTitle = `${TASKS_SECTION_LABEL_PREFIX}${tasksTotal}${TASKS_SECTION_LABEL_SUFFIX}`;
  }

  return (
    <section
      data-testid="governance-tile-tasks-block"
      className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-5"
    >
      <p
        data-testid="governance-tile-tasks-section-label"
        className="text-[11px] font-semibold tracking-wide text-slate-400"
      >
        {sectionTitle}
      </p>

      <div
        data-testid="governance-tile-severity-row"
        className="grid grid-cols-3 gap-3 my-4 p-3 rounded-lg border border-dashed border-slate-700 bg-slate-950/30"
      >
        {SEVERITY_COUNTERS.map((c) => (
          <SeverityCounter key={c.key} counter={c} summary={workingSummary} />
        ))}
      </div>

      <p className="text-[11px] font-semibold tracking-wide text-slate-400 mt-1">
        {ACTOR_STRIP_HEADING}
      </p>
      <div
        data-testid="governance-tile-actor-row"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2 mb-4 p-3 rounded-lg border border-dashed border-slate-700 bg-slate-950/30"
      >
        {ACTOR_COUNTERS.map((c) => (
          <ActorCounter key={c.key} counter={c} summary={workingSummary} />
        ))}
      </div>

      <p className="text-[11px] text-slate-400 text-right mb-2">
        {TASKS_FILTER_HINT}
      </p>

      {cards.length === 0 ? (
        <div
          data-testid="governance-tile-tasks-empty"
          className="rounded-lg border border-dashed border-slate-700 bg-slate-950/30 min-h-[160px] flex flex-col items-center justify-center text-center px-6 py-8"
        >
          <p className="text-[13px] font-semibold text-slate-200">
            {TASKS_EMPTY_TITLE}
          </p>
          <p className="mt-1 max-w-lg text-[11px] text-slate-400 leading-snug">
            {TASKS_EMPTY_BODY}
          </p>
        </div>
      ) : (
        <div
          data-testid="governance-tile-tasks-grid"
          className="grid grid-cols-1 gap-3"
        >
          {cards.map((card) => (
            <TaskCard key={card.id} card={card} />
          ))}
        </div>
      )}

      {loading ? (
        <p
          data-testid="governance-tile-tasks-loading"
          className="mt-3 text-[10px] italic text-slate-500"
        >
          Live counters loading&hellip;
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="governance-tile-tasks-error"
          className="mt-3 text-[10px] text-rose-400"
        >
          Live counter fetch failed. Counts show pending until the endpoint recovers.
        </p>
      ) : null}
    </section>
  );
}
